import React, { useContext, useEffect, useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import RadioGroup from "./RadioGroup";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import { AuthContext } from "../context/AuthProvider";
import { format } from "date-fns";
import { fetchWithInterceptor } from "../utils/fetchWithInterceptor";

const SUBCOLORS = {
  red: [
    { value: "purple", label: "Purple", color: "#471929" },
    { value: "ruby", label: "Ruby", color: "#6E1C2E" },
    { value: "garnet", label: "Garnet", color: "#5A141A" },
    { value: "tawny", label: "Tawny", color: "#822A18" },
    { value: "brown", label: "Brown", color: "#5E3D3E" },
  ],
  white: [
    { value: "straw", label: "Straw", color: "#F5F2DD" },
    { value: "yellow", label: "Yellow", color: "#F8FE8D" },
    { value: "gold", label: "Gold", color: "#FED600" },
    { value: "amber", label: "Amber", color: "#DE7E0B" },
    { value: "brown", label: "Brown", color: "#CE9A3D" },
  ],
  rose: [
    { value: "pink", label: "Pink", color: "#ED6974" },
    { value: "salmon", label: "Salmon", color: "#F1866C" },
    { value: "copper", label: "Copper", color: "#EC7F34" },
  ],
};

const EYE_VISCOSITY = [
  {
    value: "watery",
    label: "Watery",
  },
  {
    value: "midLow",
    label: "Mid-low",
  },
  {
    value: "medium",
    label: "Medium",
  },
  {
    value: "midHigh",
    label: "Mid-high",
  },
  {
    value: "high",
    label: "Syrupy",
  },
];

const INTENSITY = [
  {
    value: "low",
    label: "Low",
  },
  {
    value: "midLow",
    label: "Mid-low",
  },
  {
    value: "medium",
    label: "Medium",
  },
  {
    value: "midHigh",
    label: "Mid-high",
  },
  {
    value: "high",
    label: "High",
  },
];

const INITIAL_VALUES = {
  productName: "",
  varietal: "",
  producer: "",
  vintage: "",
  sku: "",
  retailPrice: "",
  author: "",
  date: format(new Date(), "yyyy-MM-dd"),
  eyeIntensity: "",
  eyeColor: "",
  eyeSubColor: "",
  eyeViscosity: "",
  noseIntensity: "",
  noseFruit: "",
  noseHerb: "",
  noseOak: "",
  noseEarth: "",
  mouthSweetness: "",
  mouthAcidity: "",
  mouthTannin: "",
  mouthAlcohol: "",
  mouthBody: "",
  mouthNotes: "",
  rate: "",
  comments: "",
};

const Input = (props) => (
  <TextField variant="outlined" color="primary" fullWidth {...props} />
);

export default function AddTastingDialog({ open, onClose, onSave, rowId }) {
  const [data, setData] = useState(INITIAL_VALUES);

  const authContext = useContext(AuthContext);

  const handleSave = () => {
    onSave(data);
    setData(INITIAL_VALUES);
  };

  const handleClose = () => {
    setData(INITIAL_VALUES);
    onClose();
  };

  const isValid = () =>
    !!data.productName &&
    !!data.varietal &&
    !!data.producer &&
    !!data.vintage &&
    !!data.author &&
    !!data.date &&
    !!data.rate &&
    data.rate <= 100 &&
    data.rate >= 80 &&
    !!data.comments;

  const onKeyPress = (e) => {
    console.log(e.key);
    if (!/[0-9]/.test(e.key)) {
      e.preventDefault();
    }
  };

  useEffect(() => {
    const findById = async (id) => {
      const response = await fetchWithInterceptor(`/api/tastings/${id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": authContext.getToken(),
        },
      });
      if (response.ok) {
        const data = await response.json();
        setData({ ...data });
      }
    };

    if (rowId) {
      findById(rowId);
    }
  }, [rowId, authContext]);

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={handleClose}
      aria-labelledby="max-width-dialog-title"
    >
      <DialogTitle id="max-width-dialog-title">
        {rowId ? "Edit" : "Create"} tasting
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          Fill out the following information about the product.
        </DialogContentText>
        <form noValidate autoComplete="off">
          <Box sx={{ p: 3 }}>
            {/* Information */}
            <Grid container direction="row" alignItems="flex-start">
              <Grid size={{ xs: 2 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    margin: "0px 0px 4px",
                    fontWeight: 700,
                    lineHeight: 1.55556,
                    fontSize: "1rem",
                  }}
                >
                  Information
                </Typography>
              </Grid>
              <Grid size={{ xs: 10 }}>
                <Paper
                  elevation={0}
                  variant="outlined"
                  sx={{
                    p: 3,
                    mb: 3,
                    position: "relative",
                    bgcolor: "rgb(255, 255, 255)",
                    transition:
                      "box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
                    boxShadow:
                      "rgba(145, 158, 171, 0.2) 0px 0px 2px 0px, rgba(145, 158, 171, 0.12) 0px 12px 24px -4px",
                    borderRadius: 2,
                    zIndex: 0,
                    border: "none",
                  }}
                >
                  <Grid container spacing={2} wrap="wrap">
                    <Grid size={{ xs: 8 }}>
                      <Input
                        value={data.productName}
                        onChange={(e) =>
                          setData({ ...data, productName: e.target.value })
                        }
                        label="Product name"
                        required
                      />
                    </Grid>
                    <Grid size={{ xs: 2 }}>
                      <Input
                        value={data.sku}
                        onChange={(e) =>
                          setData({ ...data, sku: e.target.value })
                        }
                        label="SKU"
                      />
                    </Grid>
                    <Grid size={{ xs: 2 }}>
                      <Input
                        value={data.retailPrice}
                        onChange={(e) =>
                          setData({ ...data, retailPrice: e.target.value })
                        }
                        label="Retail price"
                      />
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                      <Input
                        value={data.varietal}
                        onChange={(e) =>
                          setData({ ...data, varietal: e.target.value })
                        }
                        label="Varietal"
                        required
                      />
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                      <Input
                        value={data.vintage}
                        onChange={(e) =>
                          setData({ ...data, vintage: e.target.value })
                        }
                        inputProps={{ maxLength: 4, minLength: 4 }}
                        onKeyPress={onKeyPress}
                        label="Vintage"
                        required
                      />
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                      <Input
                        value={data.producer}
                        onChange={(e) =>
                          setData({ ...data, producer: e.target.value })
                        }
                        label="Producer"
                        required
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
            {/* Eye */}
            <Grid
              container
              direction="row"
              alignItems="flex-start"
              sx={{ my: 3 }}
            >
              <Grid size={{ xs: 2 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    margin: "0px 0px 4px",
                    fontWeight: 700,
                    lineHeight: 1.55556,
                    fontSize: "1rem",
                  }}
                >
                  Eye
                </Typography>
              </Grid>
              <Grid size={{ xs: 10 }}>
                <Paper
                  elevation={0}
                  variant="outlined"
                  sx={{
                    p: 3,
                    mb: 3,
                    position: "relative",
                    bgcolor: "rgb(255, 255, 255)",
                    transition:
                      "box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
                    boxShadow:
                      "rgba(145, 158, 171, 0.2) 0px 0px 2px 0px, rgba(145, 158, 171, 0.12) 0px 12px 24px -4px",
                    borderRadius: 2,
                    zIndex: 0,
                    border: "none",
                  }}
                >
                  <RadioGroup
                    label="Color Intensity"
                    value={data.eyeIntensity}
                    onChange={(value) =>
                      setData({ ...data, eyeIntensity: value })
                    }
                    data={[
                      { value: "pale", label: "Pale" },
                      { value: "medium", label: "Medium" },
                      { value: "deep", label: "Deep" },
                    ]}
                  />
                  <RadioGroup
                    label="Color"
                    value={data.eyeColor}
                    onChange={(value) =>
                      setData({ ...data, eyeColor: value, eyeSubColor: "" })
                    }
                    data={[
                      { value: "red", label: "Red" },
                      { value: "white", label: "White" },
                      { value: "rose", label: "Rose" },
                    ]}
                  />
                  {!!data.eyeColor && (
                    <RadioGroup
                      label="Sub Color"
                      value={data.eyeSubColor}
                      onChange={(value) =>
                        setData({ ...data, eyeSubColor: value })
                      }
                      data={SUBCOLORS[data.eyeColor]}
                    />
                  )}
                  <RadioGroup
                    label="Viscosity"
                    value={data.eyeViscosity}
                    onChange={(value) =>
                      setData({ ...data, eyeViscosity: value })
                    }
                    data={EYE_VISCOSITY}
                  />
                </Paper>
              </Grid>
            </Grid>
            {/* Nose */}
            <Grid
              container
              direction="row"
              alignItems="flex-start"
              sx={{ my: 3 }}
            >
              <Grid size={{ xs: 2 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    margin: "0px 0px 4px",
                    fontWeight: 700,
                    lineHeight: 1.55556,
                    fontSize: "1rem",
                  }}
                >
                  Nose
                </Typography>
              </Grid>
              <Grid size={{ xs: 10 }}>
                <Paper
                  elevation={0}
                  variant="outlined"
                  sx={{
                    p: 3,
                    mb: 3,
                    position: "relative",
                    bgcolor: "rgb(255, 255, 255)",
                    transition:
                      "box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
                    boxShadow:
                      "rgba(145, 158, 171, 0.2) 0px 0px 2px 0px, rgba(145, 158, 171, 0.12) 0px 12px 24px -4px",
                    borderRadius: 2,
                    zIndex: 0,
                    border: "none",
                  }}
                >
                  <RadioGroup
                    label="Aroma Intensity"
                    value={data.noseIntensity}
                    onChange={(value) =>
                      setData({ ...data, noseIntensity: value })
                    }
                    data={INTENSITY}
                  />
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                      <Input
                        value={data.noseFruit}
                        onChange={(e) =>
                          setData({ ...data, noseFruit: e.target.value })
                        }
                        multiline
                        minRows={2}
                        maxRows={2}
                        label="Fruit"
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Input
                        value={data.noseHerb}
                        onChange={(e) =>
                          setData({ ...data, noseHerb: e.target.value })
                        }
                        multiline
                        minRows={2}
                        maxRows={2}
                        label="Herb/Other"
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Input
                        value={data.noseOak}
                        onChange={(e) =>
                          setData({ ...data, noseOak: e.target.value })
                        }
                        multiline
                        minRows={2}
                        maxRows={2}
                        label="Oak"
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Input
                        value={data.noseEarth}
                        onChange={(e) =>
                          setData({ ...data, noseEarth: e.target.value })
                        }
                        multiline
                        minRows={2}
                        maxRows={2}
                        label="Earth"
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
            {/* Mouth */}
            <Grid
              container
              direction="row"
              alignItems="flex-start"
              sx={{ my: 3 }}
            >
              <Grid size={{ xs: 2 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    margin: "0px 0px 4px",
                    fontWeight: 700,
                    lineHeight: 1.55556,
                    fontSize: "1rem",
                  }}
                >
                  Mouth
                </Typography>
              </Grid>
              <Grid size={{ xs: 10 }}>
                <Paper
                  elevation={0}
                  variant="outlined"
                  sx={{
                    p: 3,
                    mb: 3,
                    position: "relative",
                    bgcolor: "rgb(255, 255, 255)",
                    transition:
                      "box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
                    boxShadow:
                      "rgba(145, 158, 171, 0.2) 0px 0px 2px 0px, rgba(145, 158, 171, 0.12) 0px 12px 24px -4px",
                    borderRadius: 2,
                    zIndex: 0,
                    border: "none",
                  }}
                >
                  <RadioGroup
                    label="Sweetness"
                    value={data.mouthSweetness}
                    onChange={(value) =>
                      setData({ ...data, mouthSweetness: value })
                    }
                    data={[
                      { value: "boneDry", label: "Bone dry" },
                      { value: "dry", label: "Dry" },
                      { value: "offDry", label: "Off-Dry" },
                      { value: "sweet", label: "Sweet" },
                      { value: "verySweet", label: "Very sweet" },
                    ]}
                  />
                  <RadioGroup
                    label="Acidity"
                    value={data.mouthAcidity}
                    onChange={(value) =>
                      setData({ ...data, mouthAcidity: value })
                    }
                    data={INTENSITY}
                  />
                  <RadioGroup
                    label="Tannin"
                    value={data.mouthTannin}
                    onChange={(value) =>
                      setData({ ...data, mouthTannin: value })
                    }
                    data={INTENSITY}
                  />
                  <RadioGroup
                    label="Alcohol %"
                    value={data.mouthAlcohol}
                    onChange={(value) =>
                      setData({ ...data, mouthAlcohol: value })
                    }
                    data={[
                      {
                        value: "low",
                        label: "Low",
                      },
                      {
                        value: "medium",
                        label: "Medium",
                      },
                      {
                        value: "high",
                        label: "High",
                      },
                    ]}
                  />
                  <RadioGroup
                    label="Body"
                    value={data.mouthBody}
                    onChange={(value) => setData({ ...data, mouthBody: value })}
                    data={[
                      {
                        value: "light",
                        label: "Light",
                      },
                      {
                        value: "midLight",
                        label: "Mid-light",
                      },
                      {
                        value: "medium",
                        label: "Medium",
                      },
                      {
                        value: "midFull",
                        label: "Mid-full",
                      },
                      {
                        value: "full",
                        label: "Full",
                      },
                    ]}
                  />
                  <Input
                    value={data.mouthNotes}
                    onChange={(e) =>
                      setData({ ...data, mouthNotes: e.target.value })
                    }
                    multiline
                    minRows={2}
                    maxRows={4}
                    label="Notes"
                  />
                </Paper>
              </Grid>
            </Grid>
            {/* Conclusions */}
            <Grid
              container
              direction="row"
              alignItems="flex-start"
              sx={{ my: 3 }}
            >
              <Grid size={{ xs: 2 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    margin: "0px 0px 4px",
                    fontWeight: 700,
                    lineHeight: 1.55556,
                    fontSize: "1rem",
                  }}
                >
                  Conclusions
                </Typography>
              </Grid>
              <Grid size={{ xs: 10 }}>
                <Paper
                  elevation={0}
                  variant="outlined"
                  sx={{
                    p: 3,
                    mb: 3,
                    position: "relative",
                    bgcolor: "rgb(255, 255, 255)",
                    transition:
                      "box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
                    boxShadow:
                      "rgba(145, 158, 171, 0.2) 0px 0px 2px 0px, rgba(145, 158, 171, 0.12) 0px 12px 24px -4px",
                    borderRadius: 2,
                    zIndex: 0,
                    border: "none",
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 4 }}>
                      <TextField
                        label="Internal Rate*"
                        value={data.rate}
                        onChange={(e) =>
                          setData({ ...data, rate: e.target.value })
                        }
                        sx={{ minWidth: 160 }}
                        fullWidth
                      />
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                      <TextField
                        label="Author*"
                        value={data.author}
                        onChange={(e) =>
                          setData({ ...data, author: e.target.value })
                        }
                        sx={{ minWidth: 160 }}
                        fullWidth
                      />
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                      <TextField
                        label="Date"
                        type="date"
                        value={data.date}
                        onChange={(e) =>
                          setData({ ...data, date: e.target.value })
                        }
                        slotProps={{
                          inputLabel: {
                            shrink: true,
                          },
                        }}
                        sx={{ minWidth: 160 }}
                        fullWidth
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        label="Final comments*"
                        value={data.comments}
                        onChange={(e) =>
                          setData({ ...data, comments: e.target.value })
                        }
                        multiline
                        fullWidth
                        sx={{ flex: 1, minWidth: 320 }}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={!isValid()}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
